// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import MessengerChat from "./components/MessengerChat";

// Public pages
import Home      from "./pages/public/Home";
import Services  from "./pages/public/Services";
import Packages  from "./pages/public/Packages";
import Portfolio from "./pages/public/Portfolio";
import About     from "./pages/public/About";
import Contact   from "./pages/public/Contact";
import Privacy   from "./pages/public/Privacy";
import Terms   from "./pages/public/Terms";
import DataDeletion   from "./pages/public/DataDeletion";

// Auth
import Login from "./pages/Login";

// Portal
import PortalLayout from "./pages/portal/PortalLayout";
import Dashboard    from "./pages/portal/Dashboard";
import Orders       from "./pages/portal/Orders";
import Billing      from "./pages/portal/Billing";
import Support      from "./pages/portal/Support";
import Upgrades     from "./pages/portal/Upgrades";
import TeamMember from "./pages/public/TeamMember"

// Admin
import AdminDashboard from "./pages/admin/AdminDashboard";

import "./styles/globals.css";

// ─── ADMIN ROUTE GUARD ────────────────────────────────────────────────────────
// Requires BOTH an authenticated session AND role === "admin" in Firestore.
// Non-admins who are authenticated are sent to /portal (not /login).
// Unauthenticated users are sent to /login with a redirect param.
function AdminRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="route-guard-loading">
        <div className="spinner" />
      </div>
    );
  }

  // Not logged in → login page
  if (!currentUser) {
    return <Navigate to="/login?redirect=/admin" replace />;
  }

  // Logged in but not admin → send to standard portal
  // userProfile may still be loading (null) for a brief moment after login;
  // once it resolves, if role is not "admin" we redirect.
  if (userProfile !== null && userProfile?.role !== "admin") {
    return <Navigate to="/portal" replace />;
  }

  // Still waiting for userProfile to hydrate — show spinner
  if (userProfile === null) {
    return (
      <div className="route-guard-loading">
        <div className="spinner" />
      </div>
    );
  }

  return children;
}

// ─── 404 ─────────────────────────────────────────────────────────────────────
function NotFound() {
  return (
    <div className="not-found-page">
      <div className="not-found-code">404</div>
      <h1 className="heading-1">Page not found</h1>
      <p className="text-muted">The page you're looking for doesn't exist.</p>
      <a href="/" className="btn btn-primary mt-24">Go Home</a>
    </div>
  );
}

// ─── PLACEHOLDER (Terms / Privacy) ───────────────────────────────────────────
function Placeholder({ title }) {
  return (
    <div className="placeholder-page">
      <div className="container">
        <h1 className="heading-1 mb-16">{title}</h1>
        <p className="text-muted">
          This page is being updated. Please contact us at{" "}
          <a href="mailto:info@aimbletech.com">info@aimbletech.com</a>{" "}
          for more information.
        </p>
      </div>
    </div>
  );
}

// ─── PUBLIC LAYOUT ────────────────────────────────────────────────────────────
function PublicLayout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <BackToTop />

          <MessengerChat />
          <Routes>

            {/* ── Admin route — no portal sidebar, uses public navbar ── */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Navbar />
                  <div className="admin-page-wrap">
                    <AdminDashboard />
                  </div>
                  <Footer />
                </AdminRoute>
              }
            />

            {/* ── Portal routes — own sidebar layout, no public footer ── */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute>
                  <Navbar />
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index                    element={<Dashboard />} />
              <Route path="orders"            element={<Orders />} />
              <Route path="billing"           element={<Billing />} />
              <Route path="support"           element={<Support />} />
              <Route path="support/new"       element={<Support />} />
              <Route path="upgrades"          element={<Upgrades />} />
              <Route path="DataDeletion"          element={<DataDeletion />} />
            </Route>

            {/* ── Public routes — shared navbar + footer ── */}
            <Route element={<PublicLayout />}>
              <Route path="/"          element={<Home />} />
              <Route path="/services"  element={<Services />} />
              <Route path="/packages"  element={<Packages />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/about"     element={<About />} />
              <Route path="/contact"   element={<Contact />} />
              <Route path="/login"     element={<Login />} />
              <Route path="/terms"     element={<Terms title="Terms of Service" />} />
              <Route path="/privacy"   element={<Privacy title="Privacy Policy" />} />
              <Route path="*"          element={<NotFound />} />
              <Route path="/team/:id" element={<TeamMember />} />
            </Route>

          </Routes>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}